import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AgentService } from "../../services/AgentService";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Checkbox } from "../ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { MoreHorizontal, Loader2 } from "lucide-react";
import moment from "moment";
import PaginatedTable, { PaginatedTableColumn } from "../ui/paginated-table";

interface AgentVersionsTableProps {
  agentId: number;
  apiKey: ApiKeyOrGetter;
  isVersionView: boolean;
}

interface Version {
  id: number;
  version_number: string;
  version_notes: string;
  date_created: number;
  is_latest: boolean;
}

interface Release {
  id: number;
  name: string;
  version_number: string;
  is_default: boolean;
}

const AgentVersionsTable: React.FC<AgentVersionsTableProps> = ({
  agentId,
  apiKey,
  isVersionView
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [versions, setVersions] = useState<Version[]>([]);
  const [releases, setReleases] = useState<Release[]>([]);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(true);
  const [releasesLoading, setReleasesLoading] = useState<boolean>(true);
  const [releasesModalOpen, setReleasesModalOpen] = useState<boolean>(false);
  const [draftingVersion, setDraftingVersion] = useState<string | null>(null);
  const [draftConfirmDialogOpen, setDraftConfirmDialogOpen] = useState<boolean>(false);
  const [pendingDraftVersion, setPendingDraftVersion] = useState<string | null>(null);
  const [newReleaseName, setNewReleaseName] = useState<string>("");
  const [newReleaseVersion, setNewReleaseVersion] = useState<string>("");
  const [newReleaseIsDefault, setNewReleaseIsDefault] = useState<boolean>(false);
  const [releasesSubmitting, setReleasesSubmitting] = useState<boolean>(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState<number>(1);
  const pageSize = 10;

  // Track mounted state to avoid setState on unmounted component
  const isMountedRef = useRef<boolean>(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Load versions and releases
  useEffect(() => {
    if (!agentId) return;

    setVersionsLoading(true);
    setReleasesLoading(true);

    // Load versions
    const versionsSubscription = AgentService.getAgentVersions(agentId, () => {}, apiKey, 100).subscribe(
      (payload: any) => {
        setVersions(payload.versions || []);
        setVersionsLoading(false);
      },
      (err: any) => {
        console.error("Error loading versions:", err);
        setVersionsLoading(false);
      }
    );

    // Load releases
    const releasesSubscription = AgentService.getAgentReleases(agentId, () => {}, apiKey).subscribe(
      (payload: any) => {
        setReleases(payload.releases || []);
        setReleasesLoading(false);
      },
      (err: any) => {
        console.error("Error loading releases:", err);
        setReleasesLoading(false);
      }
    );

    return () => {
      versionsSubscription.unsubscribe();
      releasesSubscription.unsubscribe();
    };
  }, [agentId, apiKey]);

  const handleDraftFromVersion = (versionNumber: string) => {
    setPendingDraftVersion(versionNumber);
    setDraftConfirmDialogOpen(true);
  };

  const confirmDraftFromVersion = () => {
    if (!pendingDraftVersion || draftingVersion !== null) return;
    
    // Keep dialog open and show loading state
    setDraftingVersion(pendingDraftVersion);
    
    AgentService.draftFromVersion(agentId, pendingDraftVersion, () => {}, apiKey).subscribe(
      () => {
        AgentService.getAgentVersionStatus(agentId, () => {}, apiKey).subscribe(
          (res: any) => {
            window.dispatchEvent(new CustomEvent("agentVersionStatusUpdated", {
              detail: { agentId, versionStatus: res }
            }));
            window.dispatchEvent(new CustomEvent("agentReloadRequested", {
              detail: { agentId }
            }));
            if (!isMountedRef.current) return;
            // Close dialog and navigate to overview tab
            setDraftConfirmDialogOpen(false);
            setDraftingVersion(null);
            setPendingDraftVersion(null);
            navigate(`/agent/${agentId}?tab=overview`);
          },
          (err: any) => {
            console.error("Error loading agent version status:", err);
            window.dispatchEvent(new CustomEvent("agentReloadRequested", {
              detail: { agentId }
            }));
            if (!isMountedRef.current) return;
            // Close dialog and navigate to overview tab even on version status error
            setDraftConfirmDialogOpen(false);
            setDraftingVersion(null);
            setPendingDraftVersion(null);
            navigate(`/agent/${agentId}?tab=overview`);
          }
        );
      },
      (err: any) => {
        console.error("Error drafting from version:", err);
        if (!isMountedRef.current) return;
        // Close dialog on error
        setDraftConfirmDialogOpen(false);
        setDraftingVersion(null);
        setPendingDraftVersion(null);
      }
    );
  };

  const handleCreateRelease = async () => {
    if (!newReleaseName.trim() || !newReleaseVersion) return;
    setReleasesSubmitting(true);
    try {
      await AgentService.upsertAgentRelease(
        agentId,
        newReleaseName.trim(),
        newReleaseVersion,
        newReleaseIsDefault,
        () => {},
        apiKey
      ).toPromise();
      setReleasesModalOpen(false);
      setReleasesSubmitting(false);
      setNewReleaseName("");
      setNewReleaseVersion("");
      setNewReleaseIsDefault(false);
      // Reload releases
      AgentService.getAgentReleases(agentId, () => {}, apiKey).subscribe(
        (payload: any) => setReleases(payload.releases || []),
        (err: any) => console.error("Error reloading releases:", err)
      );
    } catch (err) {
      console.error("Error creating release:", err);
      setReleasesSubmitting(false);
    }
  };

  const getVersionReleases = (versionNumber: string) => {
    return releases.filter(r => r.version_number === versionNumber);
  };

  // Define columns for the table
  const columns: PaginatedTableColumn<Version>[] = useMemo(() => [
    {
      key: "version_number" as keyof Version,
      header: "Version",
      width: "w-32",
      render: (version: Version) => (
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="font-mono">
            v{version.version_number}
          </Badge>
          {version.is_latest && (
            <Badge className="bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300 text-xs">
              Latest
            </Badge>
          )}
        </div>
      ),
    },
    {
      key: "version_notes" as keyof Version,
      header: "Description",
      render: (version: Version) => (
        <div className="max-w-md">
          <p className="text-sm text-foreground truncate">
            {version.version_notes || "No description"}
          </p>
        </div>
      ),
    },
    {
      key: "id" as keyof Version, // Using id as key since we need a unique key
      header: "Releases",
      render: (version: Version) => {
        const versionReleases = getVersionReleases(version.version_number);
        return (
          <div className="flex flex-wrap gap-1">
            {versionReleases.map((release) => (
              <DropdownMenu key={release.name}>
                <DropdownMenuTrigger asChild>
                  <Badge 
                    variant={release.is_default ? "default" : "secondary"}
                    className="text-xs cursor-pointer hover:opacity-80"
                    tabIndex={0}
                    role="button"
                  >
                    {release.name}
                    {release.is_default && " (default)"}
                  </Badge>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    onSelect={() => {
                      const params = new URLSearchParams(searchParams);
                      params.set("version", version.version_number);
                      navigate(`/agent/${agentId}?${params.toString()}`);
                    }}
                  >
                    View Version
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onSelect={() => handleDraftFromVersion(version.version_number)}
                    disabled={draftingVersion !== null}
                    className="text-maitai-green"
                  >
                    {draftingVersion === version.version_number ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading...
                      </>
                    ) : (
                      "Draft From This Release"
                    )}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ))}
            {versionReleases.length === 0 && (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </div>
        );
      },
    },
    {
      key: "date_created" as keyof Version,
      header: "Created",
      width: "w-48",
      render: (version: Version) => (
        <span className="text-sm text-muted-foreground">
          {moment(version.date_created * 1000).format("MMM D, YYYY h:mm A")}
        </span>
      ),
    },
    {
      key: "is_latest" as keyof Version, // Using is_latest as key for actions column
      header: "",
      width: "w-12",
      render: (version: Version) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onSelect={() => {
                const params = new URLSearchParams(searchParams);
                params.set("version", version.version_number);
                navigate(`/agent/${agentId}?${params.toString()}`);
              }}
            >
              View
            </DropdownMenuItem>
            {!version.is_latest && (
              <DropdownMenuItem
                onSelect={() => handleDraftFromVersion(version.version_number)}
                disabled={draftingVersion !== null}
                className="text-maitai-green"
              >
                {draftingVersion === version.version_number ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Draft From This"
                )}
              </DropdownMenuItem>
            )}
            {!releases.some(r => r.version_number === version.version_number) && (
              <DropdownMenuItem
                onSelect={() => {
                  const commonNames = ["prod", "staging", "demo", "release"];
                  const suggestedName = commonNames.find(name => 
                    !releases.some(r => r.name === name)
                  ) || "";
                  setNewReleaseName(suggestedName);
                  setNewReleaseVersion(version.version_number);
                  setNewReleaseIsDefault(false);
                  setReleasesModalOpen(true);
                }}
                className="text-maitai-green"
              >
                Cut Release
              </DropdownMenuItem>
            )}
            {!isVersionView && releases.some(r => r.version_number === version.version_number && !r.is_default) && (
              <DropdownMenuItem
                onSelect={async () => {
                  const release = releases.find(r => r.version_number === version.version_number && !r.is_default);
                  if (release) {
                    try {
                      await AgentService.upsertAgentRelease(
                        agentId,
                        release.name,
                        version.version_number,
                        true,
                        () => {},
                        apiKey
                      ).toPromise();
                      AgentService.getAgentReleases(agentId, () => {}, apiKey).subscribe(
                        (payload: any) => setReleases(payload.releases || []),
                        (err: any) => console.error("Error reloading releases:", err)
                      );
                    } catch (err) {
                      console.error("Error setting default release:", err);
                    }
                  }
                }}
                className="text-maitai-green"
              >
                Set as Default
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [agentId, apiKey, draftingVersion, isVersionView, navigate, releases, searchParams]);

  const isLoading = versionsLoading || releasesLoading;

  return (
    <>
      <PaginatedTable<Version>
        data={versions}
        columns={columns}
        loading={isLoading}
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        pageSize={pageSize}
        emptyTitle="No versions published yet"
        emptyDescription="Publish a version to see it here."
        showPagination={versions.length > pageSize}
        loadingRows={5}
        defaultSort={(a, b) => b.date_created - a.date_created}
      />

      {/* Create Release Modal */}
      <Dialog open={releasesModalOpen} onOpenChange={(isOpen) => { if (!isOpen) setReleasesModalOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Create Release</DialogTitle>
            <DialogDescription>
              Create a named release that points to a specific agent version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="release-name">Release Name</Label>
              <Input
                id="release-name"
                value={newReleaseName}
                onChange={(e) => setNewReleaseName(e.target.value)}
                placeholder="e.g., prod, staging, demo"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="release-version">Version</Label>
              <Select value={newReleaseVersion} onValueChange={setNewReleaseVersion}>
                <SelectTrigger>
                  <SelectValue placeholder="Select version" />
                </SelectTrigger>
                <SelectContent>
                  {versions.map((v) => (
                    <SelectItem key={v.version_number} value={v.version_number}>
                      v{v.version_number} - {v.version_notes || "No description"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="is-default" 
                checked={newReleaseIsDefault}
                onCheckedChange={(checked) => setNewReleaseIsDefault(checked === true)}
              />
              <Label htmlFor="is-default" className="text-sm font-normal">
                Set as default release
              </Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setReleasesModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleCreateRelease}
              disabled={!newReleaseName.trim() || !newReleaseVersion || releasesSubmitting}
              className="bg-maitai-green hover:bg-maitai-green/90 text-white"
            >
              {releasesSubmitting ? "Creating..." : "Create Release"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Draft From Version Confirmation Dialog */}
      <AlertDialog 
        open={draftConfirmDialogOpen} 
        onOpenChange={(open) => {
          if (!open && draftingVersion === null) {
            setDraftConfirmDialogOpen(false);
            setPendingDraftVersion(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-gc-med">
              Create Draft from Version
            </AlertDialogTitle>
            <AlertDialogDescription>
              {draftingVersion ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating draft and loading changes...
                </div>
              ) : (
                `Are you sure you want to create a draft from version ${pendingDraftVersion}? This will overwrite the current draft.`
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={draftingVersion !== null}>Cancel</AlertDialogCancel>
            <Button
              onClick={confirmDraftFromVersion}
              disabled={draftingVersion !== null}
              className="bg-maitai-green hover:bg-maitai-green/90 text-white"
            >
              {draftingVersion ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating Draft...
                </>
              ) : (
                "Create Draft"
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default AgentVersionsTable;
